import { toCanvas } from 'html-to-image';

import getFontEmbedCss from './fontEmbedCss';

export declare type Options = {
    width: number;
    height: number;
};

/**
 * A hook that provides a screenshot function that uses SVG foreignObject renderer.
 *
 * Advantages:
 *  - It provides a consistent experience across different browser settings
 *  - You can specify an exact width and height of the screenshot
 *  - The content is rendered off-screen
 *
 * Disadvantages:
 *  - It doesn't support cross-origin content
 *
 * As an alternative, consider using the `useDOMRendererScreenshot` hook.
 */
export default function useSvgForeignObjectScreenshot() {
    return {
        screenshot: async (element: HTMLElement, options: Options) => {
            const bleed = 6; // extra pixels on each side to avoid clipping

            return await toCanvas(element, {
                // Supplying the font CSS ourselves stops html-to-image from reading the
                // rules of every stylesheet on the page — see `getFontEmbedCss`.
                fontEmbedCSS: await getFontEmbedCss(),
                backgroundColor: 'white',
                width: options.width, // content size
                height: options.height,
                canvasWidth: options.width + bleed * 2,
                canvasHeight: options.height + bleed * 2,
                pixelRatio: 1,
                style: {
                    transform: `translate(${bleed}px, ${bleed}px)`,
                },
            });
        },
    };
}
